import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import axios from 'axios';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, PaymentElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { useAuth } from '@/contexts/AuthContext';
import { useCart } from '@/contexts/CartContext';
import { getSessionId } from '@/utils/helpers';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Separator } from '@/components/ui/separator';
import { ScrollArea } from '@/components/ui/scroll-area';
import { ArrowLeft, ShoppingCart, User, CreditCard, Check, Smartphone } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const stripePromise = process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY 
  ? loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY)
  : null;

// Stripe Payment Form Component
const StripePaymentForm = ({ orderId, token, navigate, clearCart }) => {
  const stripe = useStripe();
  const elements = useElements();
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState(null);
  const { t } = useTranslation();

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!stripe || !elements) return;

    setLoading(true);
    setMessage(null);

    try {
      const { error, paymentIntent } = await stripe.confirmPayment({
        elements,
        confirmParams: {
          return_url: `${window.location.origin}/order-success/${orderId}`,
        },
        redirect: 'if_required',
      });

      if (error) {
        setMessage(error.message);
        toast.error(error.message);
      } else if (paymentIntent && paymentIntent.status === 'succeeded') {
        const headers = token ? { Authorization: `Bearer ${token}` } : {};
        await axios.post(`${API}/confirm-payment`, {
          order_id: orderId,
          payment_intent_id: paymentIntent.id
        }, { headers });
        
        if (clearCart) {
          await clearCart();
        }
        
        toast.success(t('checkout.paymentSuccess'));
        navigate(`/order-success/${orderId}`);
      }
    } catch (err) {
      setMessage(t('checkout.paymentError'));
      toast.error(t('checkout.paymentError'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <PaymentElement />
      {message && (
        <div className="p-3 bg-red-50 text-red-700 rounded-lg text-sm">
          {message}
        </div>
      )}
      <Button 
        type="submit" 
        size="lg" 
        className="w-full bg-amber-600 hover:bg-amber-700" 
        disabled={!stripe || loading}
        data-testid="stripe-pay-button"
      >
        {loading ? t('checkout.processing') : t('checkout.confirmPayment')}
      </Button>
      <p className="text-xs text-amber-600 text-center">
        {t('checkout.paymentByStripe')}
      </p>
    </form>
  );
};

const CheckoutPage = () => {
  const { cart, clearCart } = useCart();
  const { user, token } = useAuth();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [guestEmail, setGuestEmail] = useState('');
  const [address, setAddress] = useState({
    name: '',
    phone: '',
    country: '',
    province: '',
    city: '',
    district: '',
    address: '',
    postal_code: '',
  });
  const [note, setNote] = useState('');
  const [step, setStep] = useState('address');
  const [orderId, setOrderId] = useState(null);
  const [clientSecret, setClientSecret] = useState(null);
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState('paypal');

  // 获取支付方式列表
  useEffect(() => {
    const fetchPaymentMethods = async () => {
      try {
        const res = await axios.get(`${API}/payments/methods`);
        const methods = res.data.methods || [];
        setPaymentMethods(methods);
        // 默认选中第一个启用的支付方式
        const firstEnabled = methods.find(m => m.enabled);
        if (firstEnabled) {
          setSelectedPaymentMethod(firstEnabled.id);
        }
      } catch (err) {
        console.error('Failed to fetch payment methods', err);
      }
    };
    fetchPaymentMethods();
  }, []);

  // 获取支付方式的本地化名称
  const getPaymentMethodName = (method) => {
    if (i18n.language === 'en') return method.name_en || method.name;
    if (i18n.language === 'ru') return method.name_ru || method.name;
    return method.name;
  };

  // 支付方式图标
  const PaymentIcon = ({ methodId }) => {
    if (methodId === 'alipay') {
      return (
        <svg viewBox="0 0 24 24" className="w-6 h-6" fill="#1677FF">
          <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm5.5 14.5c-1.5.5-3.5-1-5.5-3-1.5 1-3 2-4.5 2.5-.5.2-1-.1-1.2-.6-.2-.5.1-1 .6-1.2 1.2-.4 2.5-1.2 3.7-2.1-.8-1-1.5-2.1-2-3.2H7c-.4 0-.7-.3-.7-.7s.3-.7.7-.7h2.5V6c0-.4.3-.7.7-.7s.7.3.7.7v1.5h3c.4 0 .7.3.7.7s-.3.7-.7.7h-1.3c.4.9 1 1.8 1.7 2.6 1.3-.6 2.7-1 4-1 .5 0 .9.4.9.9s-.4.9-.9.9c-1 0-2.1.3-3.1.8 1.5 1.3 3 2.3 4.2 1.9.5-.2 1 .1 1.2.6.2.5-.1 1-.6 1.2z"/>
        </svg>
      );
    }
    if (methodId === 'wechat') {
      return (
        <svg viewBox="0 0 24 24" className="w-6 h-6" fill="#07C160">
          <path d="M8.5 11c-.8 0-1.5-.7-1.5-1.5S7.7 8 8.5 8s1.5.7 1.5 1.5S9.3 11 8.5 11zm4 0c-.8 0-1.5-.7-1.5-1.5S11.7 8 12.5 8s1.5.7 1.5 1.5-.7 1.5-1.5 1.5zm-1-9C6.5 2 2 5.8 2 10.5c0 2.6 1.4 5 3.6 6.5L5 19.5c-.1.3.2.5.5.4l2.8-1.2c.9.2 1.8.3 2.7.3 5 0 9-3.8 9-8.5S16.5 2 11.5 2zm6 15c-.6 0-1-.4-1-1s.4-1 1-1 1 .4 1 1-.4 1-1 1zm3 0c-.6 0-1-.4-1-1s.4-1 1-1 1 .4 1 1-.4 1-1 1z"/>
        </svg>
      );
    }
    return <CreditCard className="w-6 h-6 text-amber-700/80" />;
  };

  if (cart.items.length === 0) {
    return (
      <div className="min-h-screen bg-amber-50/50 flex items-center justify-center">
        <Card className="max-w-md text-center p-8">
          <ShoppingCart className="w-16 h-16 mx-auto text-amber-500 mb-4" />
          <h2 className="text-xl font-bold mb-2">{t('cart.empty')}</h2>
          <p className="text-amber-600 mb-4">{t('orders.goShopping')}</p>
          <Button onClick={() => navigate('/')} className="bg-amber-600 hover:bg-amber-700">{t('cart.goShopping')}</Button>
        </Card>
      </div>
    );
  }

  const handleCreateOrder = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const sessionId = getSessionId();
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      
      // Format cart items to match OrderItem model
      const formattedItems = cart.items.map(item => ({
        product_id: item.product_id,
        product_name: item.product_name,
        product_image: item.product_image,
        price: item.product_price ?? item.price ?? 0,
        quantity: item.quantity
      }));
      
      const orderData = {
        shipping_address: address,
        items: formattedItems,
        note,
        session_id: sessionId,
        guest_email: !user ? guestEmail : null,
      };

      const orderRes = await axios.post(`${API}/orders`, orderData, { headers });
      setOrderId(orderRes.data.id);
      
      if (selectedPaymentMethod === 'paypal') {
        // PayPal flow: redirect to PayPal
        const paypalRes = await axios.post(`${API}/paypal/create-order`, {
          order_id: orderRes.data.id,
          origin_url: window.location.origin,
          order_type: 'checkout',
        }, { headers });
        window.location.href = paypalRes.data.approve_url;
      } else {
        // Stripe flow
        const paymentRes = await axios.post(`${API}/create-payment-intent`, 
          { order_id: orderRes.data.id }, 
          { headers }
        );
        
        setClientSecret(paymentRes.data.client_secret);
        setStep('payment');
        toast.success(t('checkout.orderCreated'));
      }
    } catch (err) {
      const errorMsg = err.response?.data?.detail;
      // Handle error detail that might be an array or object
      const displayError = typeof errorMsg === 'string' 
        ? errorMsg 
        : (Array.isArray(errorMsg) ? errorMsg.map(e => e.msg || e).join(', ') : t('common.error'));
      toast.error(displayError);
    } finally {
      setLoading(false);
    }
  };

  const appearance = {
    theme: 'stripe',
    variables: {
      colorPrimary: '#16a34a',
    },
  };

  const options = {
    clientSecret,
    appearance,
  };

  return (
    <div className="min-h-screen bg-amber-50/50 pt-16 py-8" data-testid="checkout-page">
      <div className="container mx-auto px-4">
        <Button variant="ghost" onClick={() => step === 'payment' ? setStep('address') : navigate(-1)} className="mb-6">
          <ArrowLeft className="w-4 h-4 mr-2" /> {t('common.back')}
        </Button>
        
        {/* Step Indicator */}
        <div className="flex items-center justify-center mb-8">
          <div className={`flex items-center ${step === 'address' ? 'text-amber-600' : 'text-amber-500'}`}>
            <div className={`w-8 h-8 rounded-full flex items-center justify-center ${step === 'address' ? 'bg-amber-600 text-white' : 'bg-amber-100 text-amber-600'}`}>
              1
            </div>
            <span className="ml-2 font-medium">{t('checkout.step1')}</span>
          </div>
          <div className="w-16 h-0.5 bg-amber-300 mx-4"></div>
          <div className={`flex items-center ${step === 'payment' ? 'text-amber-600' : 'text-amber-500'}`}>
            <div className={`w-8 h-8 rounded-full flex items-center justify-center ${step === 'payment' ? 'bg-amber-600 text-white' : 'bg-amber-200 text-amber-600'}`}>
              2
            </div>
            <span className="ml-2 font-medium">{t('checkout.step2')}</span>
          </div>
        </div>

        <h1 className="text-2xl font-bold mb-8">{step === 'address' ? t('checkout.title') : t('checkout.payment')}</h1>
        
        <div className="grid lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2">
            {step === 'address' ? (
              <form onSubmit={handleCreateOrder}>
                {!user && (
                  <Card className="mb-6">
                    <CardHeader>
                      <CardTitle>{t('checkout.contactInfo')}</CardTitle>
                      <CardDescription>{t('checkout.contactInfoDesc')}</CardDescription>
                    </CardHeader>
                    <CardContent>
                      <div className="flex gap-4 mb-4">
                        <Button type="button" variant="outline" onClick={() => navigate('/login')} className="flex-1">
                          <User className="w-4 h-4 mr-2" /> {t('checkout.loginOrRegister')}
                        </Button>
                      </div>
                      <Separator className="my-4" />
                      <p className="text-sm text-amber-600 mb-4">{t('checkout.guestCheckout')}</p>
                      <div className="space-y-2">
                        <Label htmlFor="guestEmail">{t('checkout.email')}</Label>
                        <Input
                          id="guestEmail"
                          type="email"
                          value={guestEmail}
                          onChange={(e) => setGuestEmail(e.target.value)}
                          placeholder={t('checkout.emailPlaceholder')}
                          required={!user}
                          data-testid="guest-email-input"
                        />
                      </div>
                    </CardContent>
                  </Card>
                )}

                <Card className="mb-6">
                  <CardHeader>
                    <CardTitle>{t('checkout.shippingAddress')}</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="recipientName">{t('checkout.recipientName')}</Label>
                        <Input
                          id="recipientName"
                          value={address.name}
                          onChange={(e) => setAddress({ ...address, name: e.target.value })}
                          placeholder={t('checkout.recipientNamePlaceholder')}
                          required
                          data-testid="recipient-name-input"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="recipientPhone">{t('checkout.phone')}</Label>
                        <Input
                          id="recipientPhone"
                          type="tel"
                          value={address.phone}
                          onChange={(e) => setAddress({ ...address, phone: e.target.value })}
                          placeholder={t('checkout.phonePlaceholder')}
                          required
                          data-testid="recipient-phone-input"
                        />
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="country">{t('checkout.country', 'Country / 国家')}</Label>
                        <Input
                          id="country"
                          value={address.country}
                          onChange={(e) => setAddress({ ...address, country: e.target.value })}
                          placeholder={t('checkout.countryPlaceholder', 'e.g. China, USA, Kazakhstan')}
                          required
                          data-testid="country-input"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="province">{t('checkout.province', 'State/Province / 省/州')}</Label>
                        <Input
                          id="province"
                          value={address.province}
                          onChange={(e) => setAddress({ ...address, province: e.target.value })}
                          placeholder={t('checkout.provincePlaceholder', 'e.g. Beijing, California')}
                          required
                          data-testid="province-input"
                        />
                      </div>
                    </div>
                    <div className="grid grid-cols-3 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="city">{t('checkout.city', 'City / 城市')}</Label>
                        <Input
                          id="city"
                          value={address.city}
                          onChange={(e) => setAddress({ ...address, city: e.target.value })}
                          placeholder={t('checkout.city')}
                          required
                          data-testid="city-input"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="district">{t('checkout.district', 'District / 区县')}</Label>
                        <Input
                          id="district"
                          value={address.district}
                          onChange={(e) => setAddress({ ...address, district: e.target.value })}
                          placeholder={t('checkout.districtPlaceholder', 'Optional / 选填')}
                          data-testid="district-input"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="postalCode">{t('checkout.postalCode', 'Postal Code / 邮编')}</Label>
                        <Input
                          id="postalCode"
                          value={address.postal_code}
                          onChange={(e) => setAddress({ ...address, postal_code: e.target.value })}
                          placeholder={t('checkout.postalCodePlaceholder', 'e.g. 100000')}
                          data-testid="postal-code-input"
                        />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="detailAddress">{t('checkout.detailAddress')}</Label>
                      <Textarea
                        id="detailAddress"
                        value={address.address}
                        onChange={(e) => setAddress({ ...address, address: e.target.value })}
                        placeholder={t('checkout.detailAddressPlaceholder')}
                        required
                        data-testid="detail-address-input"
                      />
                    </div>
                  </CardContent>
                </Card>

                <Card className="mb-6">
                  <CardHeader>
                    <CardTitle>{t('checkout.orderNote')}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <Textarea
                      value={note}
                      onChange={(e) => setNote(e.target.value)}
                      placeholder={t('checkout.notePlaceholder')}
                      data-testid="order-note-input"
                    />
                  </CardContent>
                </Card>

                {/* Payment Method Selection - in Step 1 */}
                <Card className="mb-6">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <CreditCard className="w-5 h-5" />
                      {t('checkout.selectPaymentMethod', '选择支付方式')}
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="grid gap-3">
                      {paymentMethods.map((method) => (
                        <div
                          key={method.id}
                          className={`p-4 border-2 rounded-lg cursor-pointer transition-all ${
                            selectedPaymentMethod === method.id
                              ? 'border-amber-600 bg-amber-50'
                              : method.enabled
                                ? 'border-amber-200 hover:border-amber-300'
                                : 'border-amber-100 bg-amber-50/50 opacity-60 cursor-not-allowed'
                          }`}
                          onClick={() => method.enabled && setSelectedPaymentMethod(method.id)}
                          data-testid={`payment-method-${method.id}`}
                        >
                          <div className="flex items-center gap-3">
                            <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                              selectedPaymentMethod === method.id ? 'border-amber-600' : 'border-amber-300'
                            }`}>
                              {selectedPaymentMethod === method.id && (
                                <div className="w-3 h-3 rounded-full bg-amber-600" />
                              )}
                            </div>
                            <PaymentIcon methodId={method.id} />
                            <div className="flex-1">
                              <p className="font-medium">{getPaymentMethodName(method)}</p>
                              <p className="text-sm text-amber-600">{method.description}</p>
                            </div>
                            {!method.enabled && (
                              <span className="text-xs bg-amber-200 text-amber-700/80 px-2 py-1 rounded">
                                {t('checkout.comingSoon', '即将开通')}
                              </span>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>

                <Button type="submit" size="lg" className="w-full bg-amber-600 hover:bg-amber-700" disabled={loading} data-testid="proceed-to-payment-button">
                  {loading ? t('checkout.processing') : `${t('checkout.proceedToPayment')} ¥${(cart.total ?? 0).toFixed(2)}`}
                </Button>
              </form>
            ) : (
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <CreditCard className="w-5 h-5" />
                    {t('checkout.completePayment', '完成支付')}
                  </CardTitle>
                  <CardDescription>
                    {t('checkout.securePaymentDesc')}
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  {/* Stripe Payment Form */}
                  {selectedPaymentMethod === 'stripe' && clientSecret && stripePromise && (
                    <Elements stripe={stripePromise} options={options}>
                      <StripePaymentForm orderId={orderId} token={token} navigate={navigate} clearCart={clearCart} />
                    </Elements>
                  )}

                  {/* Alipay / WeChat Pay placeholder */}
                  {(selectedPaymentMethod === 'alipay' || selectedPaymentMethod === 'wechat') && (
                    <div className="text-center py-8">
                      <Smartphone className="w-12 h-12 mx-auto text-amber-500 mb-4" />
                      <p className="text-amber-600 mb-4">
                        {selectedPaymentMethod === 'alipay' 
                          ? t('checkout.alipayNotReady', '支付宝支付正在接入中，请选择其他支付方式')
                          : t('checkout.wechatNotReady', '微信支付正在接入中，请选择其他支付方式')
                        }
                      </p>
                    </div>
                  )}
                </CardContent>
              </Card>
            )}
          </div>

          {/* Order Summary */}
          <div>
            <Card className="sticky top-24">
              <CardHeader>
                <CardTitle>{t('checkout.orderSummary')}</CardTitle>
              </CardHeader>
              <CardContent>
                <ScrollArea className="max-h-64">
                  {cart.items.map((item) => {
                    const price = item.product_price ?? item.price ?? 0;
                    const subtotal = item.subtotal ?? (price * item.quantity);
                    return (
                      <div key={item.product_id} className="flex gap-3 py-3 border-b">
                        <img src={item.product_image} alt={item.product_name} className="w-16 h-16 object-cover rounded" />
                        <div className="flex-1">
                          <p className="text-sm font-medium line-clamp-2">{item.product_name}</p>
                          <p className="text-sm text-amber-600">x{item.quantity}</p>
                        </div>
                        <p className="font-medium">¥{subtotal.toFixed(2)}</p>
                      </div>
                    );
                  })}
                </ScrollArea>
                <Separator className="my-4" />
                <div className="space-y-2">
                  <div className="flex justify-between">
                    <span className="text-amber-600">{t('checkout.subtotal')}</span>
                    <span>¥{(cart.total ?? 0).toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-amber-600">{t('checkout.shipping')}</span>
                    <span className="text-amber-600">{t('checkout.freeShipping')}</span>
                  </div>
                  <Separator />
                  <div className="flex justify-between text-lg font-bold">
                    <span>{t('checkout.total')}</span>
                    <span className="text-amber-600">¥{(cart.total ?? 0).toFixed(2)}</span>
                  </div>
                </div>
                {step === 'payment' && (
                  <div className="mt-4 p-3 bg-amber-50 rounded-lg">
                    <p className="text-sm text-amber-700 flex items-center gap-2">
                      <Check className="w-4 h-4" />
                      {t('checkout.orderCreated')}
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CheckoutPage;
